import React, { useState } from 'react';
import BottomSheet from './BottomSheet';

function fmtDate(s) {
  const str = String(s ?? '');
  if (str.length === 8) return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
  return str;
}

function truncateText(value, max = 56) {
  const text = String(value ?? '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max)}...`;
}

function getSafeCategory(value, fallback = '') {
  const text = String(value ?? '').trim();
  return text || fallback;
}

function hasMedicationCategory(value) {
  const text = String(value ?? '').trim();
  return !!text && text !== '미분류';
}

function countChipStyle() {
  return {
    fontSize: 11,
    fontWeight: 700,
    color: '#1d4ed8',
    background: '#eff6ff',
    border: '1px solid #dbeafe',
    borderRadius: 999,
    padding: '3px 8px',
    whiteSpace: 'nowrap',
  };
}

function normalizeCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function NhisMedicationSummary({ medication }) {
  const [open, setOpen] = useState(false);

  if (!medication) return null;

  const {
    totalPrescriptions = 0,
    uniqueDrugs = 0,
    latestDate,
    topRepeated = [],
    categoryDistribution = [],
    efficacyDistribution = [],
    dispenseTypeDistribution = [],
    recentBundle,
  } = medication;

  const normalizedEfficacy = Array.isArray(efficacyDistribution) && efficacyDistribution.length > 0
    ? efficacyDistribution
    : (Array.isArray(categoryDistribution) ? categoryDistribution : []);
  const filteredEfficacy = normalizedEfficacy.filter((item) => hasMedicationCategory(item?.category));

  const normalizedDispenseTypes = Array.isArray(dispenseTypeDistribution) ? dispenseTypeDistribution : [];
  const normalizedTopRepeated = Array.isArray(topRepeated) ? topRepeated : [];

  const summaryText = [
    `총 투약 ${totalPrescriptions}건`,
    `약품 ${uniqueDrugs}종`,
    latestDate ? `최신 ${fmtDate(latestDate)}` : null,
  ].filter(Boolean).join(' · ');

  const previewRepeated = normalizedTopRepeated.slice(0, 2);
  const previewEfficacy = filteredEfficacy.slice(0, 3);
  const maxCount = filteredEfficacy[0]?.count ?? 1;

  const subHeader = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      <span style={{ ...countChipStyle(), color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        투약 {totalPrescriptions}건
      </span>
      <span style={{ ...countChipStyle(), color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
        약품 {uniqueDrugs}종
      </span>
      {latestDate && (
        <span style={{ ...countChipStyle(), color: '#334155', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          최신 {fmtDate(latestDate)}
        </span>
      )}
    </div>
  );

  return (
    <>
      <div style={{
        background: '#fff',
        border: '1px solid #dce7f2',
        borderRadius: 18,
        boxShadow: '0 10px 30px rgba(13,36,67,0.10)',
        padding: 14,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}>
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 760, letterSpacing: '-0.01em', color: '#102036' }}>
            투약 패턴 요약
          </span>
          <p style={{ margin: '5px 0 0', fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
            {summaryText}
          </p>
        </div>

        <div style={{ marginBottom: 8 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#334155' }}>
            반복 처방
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 11, color: '#64748b' }}>
            2회 이상 반복된 약품 {normalizedTopRepeated.length}종
          </p>
        </div>

        {previewRepeated.length > 0 && (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 7 }}>
            {previewRepeated.map((drug) => (
              <li key={drug.name} style={{
                padding: '10px 11px',
                borderRadius: 10,
                border: '1px solid #e6edf7',
                background: '#f8fafc',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 8,
                alignItems: 'center',
              }}>
                <div>
                  <b style={{ fontSize: 13, color: '#0f172a' }} title={drug.name}>
                    {truncateText(drug.name, 48)}
                  </b>
                  {hasMedicationCategory(drug.category) && (
                    <small style={{ display: 'block', marginTop: 2, fontSize: 11, color: '#64748b' }}>
                      {getSafeCategory(drug.category)}
                    </small>
                  )}
                </div>
                <span style={countChipStyle()}>{drug.count}회</span>
              </li>
            ))}
          </ul>
        )}

        {previewRepeated.length === 0 && (
          <div style={{
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            background: '#f8fafc',
            padding: '11px 12px',
            fontSize: 12,
            color: '#64748b',
          }}>
            반복 처방 약품이 없습니다.
          </div>
        )}

        {previewEfficacy.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#334155' }}>주요 효능군</p>
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {previewEfficacy.map((cat) => (
                <span
                  key={`${cat.category}-${cat.count}`}
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#475569',
                    border: '1px solid #dbe5f1',
                    borderRadius: 999,
                    background: '#f8fafc',
                    padding: '3px 8px',
                  }}
                >
                  {getSafeCategory(cat.category)} {cat.count}
                </span>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={() => setOpen(true)}
          style={{
            marginTop: 12,
            width: '100%',
            height: 36,
            borderRadius: 8,
            border: '1px solid #1763ff',
            color: '#1763ff',
            background: 'transparent',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          투약 상세
        </button>
      </div>

      <BottomSheet open={open} onClose={() => setOpen(false)} title="투약 상세" subHeader={subHeader}>
        <div style={{
          border: '1px solid #e6edf8',
          borderRadius: 12,
          background: '#f9fcff',
          padding: 10,
          display: 'grid',
          gap: 10,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 760, color: '#18395f' }}>효능군 분포</span>
              <span style={{ fontSize: 11, color: '#6f8299' }}>처방 빈도</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(filteredEfficacy ?? []).map((cat) => {
                const currentCount = normalizeCount(cat.count);
                const pct = Math.round((currentCount / maxCount) * 100);
                return (
                  <div
                    key={`${cat.category}-${cat.count}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(96px, 112px) minmax(0, 1fr) auto',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <b
                      style={{
                        fontSize: 11,
                        lineHeight: 1.35,
                        color: '#374151',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={getSafeCategory(cat.category)}
                    >
                      {getSafeCategory(cat.category)}
                    </b>
                    <div style={{ height: 8, borderRadius: 999, background: '#e8eef7', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: '#2563eb',
                        borderRadius: 'inherit',
                      }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 710, color: '#37547c', whiteSpace: 'nowrap' }}>
                      {currentCount}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {normalizedDispenseTypes.length > 0 && <div style={{ height: 1, background: '#e5edf8' }} />}

          {normalizedDispenseTypes.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 760, color: '#18395f' }}>조제 유형 분포</span>
                <span style={{ fontSize: 11, color: '#6f8299' }}>투약 건수</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {normalizedDispenseTypes.map((item) => (
                  <span
                    key={`${item.category}-${item.count}`}
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#334155',
                      border: '1px solid #dbe5f1',
                      borderRadius: 999,
                      background: '#fff',
                      padding: '4px 9px',
                    }}
                  >
                    {getSafeCategory(item.category, '기타')} {item.count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {normalizedTopRepeated.length > 0 && <div style={{ height: 1, background: '#e5edf8' }} />}

          {normalizedTopRepeated.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 760, color: '#18395f' }}>반복 처방 약품</span>
                <span style={{ fontSize: 11, color: '#6f8299' }}>2회 이상</span>
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
                {normalizedTopRepeated.map((drug) => (
                  <li key={drug.name} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 12px',
                    border: '1px solid #e6edf7',
                    background: '#fff',
                    borderRadius: 11,
                    overflow: 'hidden',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          fontWeight: 700,
                          color: '#111827',
                          lineHeight: 1.4,
                          wordBreak: 'break-word',
                        }}
                      >
                        {drug.name}
                      </p>
                      {hasMedicationCategory(drug.category) && (
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6b7280' }}>
                          {getSafeCategory(drug.category)}
                        </p>
                      )}
                    </div>
                    <span style={{ ...countChipStyle(), flexShrink: 0 }}>{drug.count}회</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {recentBundle && <div style={{ height: 1, background: '#e5edf8' }} />}

          {recentBundle && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 760, color: '#18395f' }}>최근 처방 묶음</span>
                <span style={{ fontSize: 11, color: '#6f8299' }}>
                  {fmtDate(recentBundle.date)} · {recentBundle.facility}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(recentBundle.items ?? []).map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '8px 12px',
                    border: '1px solid #e6edf7',
                    background: '#fff',
                    borderRadius: 11,
                    gap: 10,
                    overflow: 'hidden',
                  }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, color: '#111827', fontWeight: 600, lineHeight: 1.4, wordBreak: 'break-word' }}>
                        {item.name}
                      </p>
                      {hasMedicationCategory(item.category) && (
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6b7280' }}>
                          {getSafeCategory(item.category)}
                        </p>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {item.days != null ? `${item.days}일분` : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </BottomSheet>
    </>
  );
}
